'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { ChevronDown } from 'lucide-react';
import { SiteHeader } from '@/components/public/site-header';
import { Footer } from '@/components/public/footer';
import { NewsletterSubscribeForm } from '@/components/public/newsletter-subscribe-form';
import { useFaqCount } from '@/lib/faq-hooks';

function FAQAccordion() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const t = useTranslations('newsletterPage');
  const faqCount = useFaqCount('newsletterPage.faq');

  const faqs = Array.from({ length: faqCount }, (_, idx) => idx + 1).map((i) => ({
    question: t(`faq.q${i}`),
    answer: t(`faq.a${i}`),
  }));

  return (
    <div className="space-y-4">
      {faqs.map((faq, index) => (
        <div
          key={index}
          className="bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-2xl"
        >
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="w-full px-8 py-6 text-left flex items-center justify-between gap-4 hover:bg-[#faf8f6] transition-colors duration-300"
          >
            <span className="font-poppins text-xl md:text-2xl font-semibold text-gray-900 pr-4">
              {faq.question}
            </span>
            <div
              className={`flex-shrink-0 w-10 h-10 rounded-full bg-[#801515] flex items-center justify-center text-white transition-transform duration-500 ${
                openIndex === index ? 'rotate-180' : ''
              }`}
            >
              <ChevronDown className="w-6 h-6" />
            </div>
          </button>
          <div
            className={`transition-all duration-500 ease-in-out ${
              openIndex === index ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
            }`}
            style={{ overflow: 'hidden' }}
          >
            <div className="px-8 pb-6 pt-2">
              <div className="w-16 h-1 bg-[#801515] mb-4" />
              <p className="text-lg text-gray-600 leading-relaxed">{faq.answer}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function NewsletterPage() {
  const t = useTranslations('newsletterPage');
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <SiteHeader />
      <main className="min-h-screen">
        {/* Hero Section */}
        <section className="relative h-screen w-full overflow-hidden">
          <div
            className="absolute inset-0 transition-transform duration-100"
            style={{ transform: `translateY(${scrollY * 0.5}px)` }}
          >
            <Image
              src="/design-images/uploads/1779266932887-d_01.jpg"
              alt="Letters from the Castle newsletter"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60" />
          </div>

          <div className="relative h-full flex flex-col items-center justify-center px-6 text-center">
            <div className="space-y-6 max-w-3xl mx-auto observe-me slide-up">
              {/* Main Title - Line 1 */}
              <h1
                className="font-poppins text-3xl sm:text-5xl md:text-7xl font-bold text-white leading-tight"
                style={{ textShadow: '2px 2px 30px rgba(0,0,0,0.5)' }}
              >
                {t('hero.title1')}
              </h1>

              {/* Decorative Line with Animation */}
              <div className="flex items-center justify-center gap-4 mb-2">
                <div className="h-[2px] w-20 bg-[#801515]" style={{ animation: 'pulse 3s ease-in-out infinite' }} />
                <p className="text-base md:text-lg uppercase tracking-[0.3em] text-white/90" style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.5)' }}>
                  {t('hero.title2')}
                </p>
                <div className="h-[2px] w-20 bg-[#801515]" style={{ animation: 'pulse 3s ease-in-out infinite', animationDelay: '1.5s' }} />
              </div>

              {/* Title - Line 3 */}
              <p
                className="text-3xl md:text-4xl text-white/95 max-w-3xl mx-auto font-light"
                style={{ textShadow: '1px 1px 8px rgba(0,0,0,0.5)' }}
              >
                {t('hero.title3')}
              </p>
            </div>

            {/* Scroll indicator */}
            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/60">
              <span className="text-xs uppercase tracking-widest">{t('hero.explore')}</span>
              <div className="w-6 h-10 border-2 border-white/40 rounded-full flex items-start justify-center p-1.5">
                <div className="w-1.5 h-3 bg-white/60 rounded-full" style={{ animation: 'float 2s ease-in-out infinite' }} />
              </div>
            </div>
          </div>
        </section>

        {/* Description & Newsletter Form Section - Side by Side */}
        <section className="design-section-gap bg-gradient-to-b from-white via-white to-[#faf8f6] py-20 sm:py-28">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
              {/* Left Column - Description Text */}
              <div className="flex flex-col justify-center">
                <div className="observe-me slide-up">
                  <h2 className="font-poppins text-[clamp(1.75rem,2.5vw,2.5rem)] font-semibold text-gray-900 mb-4 leading-tight">
                    {t('description.title')}
                  </h2>
                  <div className="w-16 h-1 bg-[#801515] mb-8" />
                </div>
                <div className="space-y-6 text-gray-700 leading-relaxed observe-me slide-up" style={{ animationDelay: '0.15s' }}>
                  <p className="text-lg md:text-xl text-gray-600">
                    {t('description.para1')}
                  </p>
                  <p className="text-lg md:text-xl text-gray-600">
                    {t('description.para2')}
                  </p>
                  <p className="text-lg md:text-xl text-gray-600">
                    {t('description.para3')}
                  </p>
                </div>
              </div>

              {/* Right Column - Newsletter Form */}
              <div className="flex flex-col justify-center observe-me slide-up" style={{ animationDelay: '0.3s' }}>
                <h2 className="font-poppins text-3xl sm:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                  {t('title')}
                </h2>
                <div className="w-16 h-1 bg-[#801515] mb-6" />
                <p className="text-lg text-gray-600 leading-relaxed mb-10">
                  {t('subtitle')}
                </p>
                <NewsletterSubscribeForm source="newsletter-page" />
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="relative py-24 lg:py-32 bg-gradient-to-b from-white to-[#faf8f6]">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6">
                <span className="text-[#801515]">{t('faq.heading')}</span>
                <br />
                <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                  {t('faq.subheading')}
                </span>
              </h2>
              <div className="w-16 h-1 bg-[#801515] mx-auto" />
            </div>

            <FAQAccordion />
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
